import { IconButton } from "../../../../components/IconButton";
import { css, styled } from "../../../../styles";

// Compact input treatments for the side-panel cards. They live here (rather than inside
// PatientMenuCard) so the custom-field editor can reuse the exact same sizing — a card input that
// keeps the TextField/Select defaults renders at 200x42 and towers over its neighbours.
export const textFieldCss = css({
  padding: "3px 0 0 4px",
  width: "143px",
  color: "$black !important",

  input: {
    height: "20px",
    width: "calc(100% - 13px)",
    padding: "1px 4px 2px 7px",
    fontSize: "12px",
    cursor: "text",
  },

  span: {
    marginBottom: "0",
  },

  p: {
    paddingLeft: "0",
    marginLeft: "0",
  },
});

// EN-5067: the chevron rules below are shared by both card dropdowns. antd positions the arrow
// absolutely against the `.ant-select` ROOT, so it only lands on the box's right edge if the root
// and the selector share the same geometry — hence the 8px inset lives on the root, not on the
// selector (which used to carry `margin-left: 8px` + `width: 100%` and so overflowed the root,
// inconsistently: it shrank back when the content was short and didn't when it was a long
// placeholder). And antd sizes the root to `controlHeight` (32px) while we squash the selector to
// 25px, which left the arrow centred on the wrong box.
const cardSelectBoxCss = {
  // Pins the root to the selector's height so `top: 50%` centres the arrow on the visible box.
  "&.ant-select": {
    height: "25px !important",
    margin: "4px 0 0 8px",
  },

  // ArrowDown is a 24px svg with its 10px glyph centred inside it, so it carries 7px of invisible
  // padding on each side. antd's 11px inset is sized for an icon that fills its box, which pushed
  // the visible chevron 18px off the edge — adrift in the middle of the box next to a truncated
  // placeholder. 3px puts the glyph exactly on the selector's 8px padding edge, mirroring the
  // text's inset on the left, and leaves antd's 18px text reservation as the gap between them.
  ".ant-select-arrow": {
    top: "50% !important",
    insetInlineEnd: "3px !important",
    margin: "0 !important",
    transform: "translateY(-50%) !important",
  },
} as const;

export const selectCss = css({
  color: "$black !important",

  ...cardSelectBoxCss,

  ".ant-select-selector": {
    height: `25px !important`,
    alignItems: "center",
    border: "solid 2px $gray-50 !important",
    boxShadow: "0 0 0 2px $gray-50 !important",
    padding: "0 8px !important",
    position: "initial !important",

    "&:hover": {
      borderColor:
        "var(--colors-patient-details-edit-dropdown-hover) !important",
    },

    span: {
      "&.ant-select-selection-item": {
        fontFamily: "Open Sans",
        fontSize: "12px",
        borderRadius: "14px !important",
      },
    },
  },

  ".ant-select-item-option": {
    ".ant-select-item-option-content": {
      fontSize: "12px !important",
      fontWeight: "400",
    },
  },
});

// Multi-select variant of selectCss (used by the Ward card and custom Multiselect fields). Same
// 25px box, border and spacing as the single-select dropdown so it lines up with its neighbours;
// the extra rules shrink the selected-value tags so they fit inside the short 25px selector.
export const multiSelectCss = css({
  color: "$black !important",

  ...cardSelectBoxCss,

  // In multiple mode the placeholder is absolutely positioned and gets none of the arrow
  // reservation single mode applies, so bound it short of the chevron by hand.
  "&.ant-select-multiple .ant-select-selection-placeholder": {
    insetInlineEnd: "28px !important",
  },

  ".ant-select-selector": {
    height: `25px !important`,
    alignItems: "center",
    border: "solid 2px $gray-50 !important",
    boxShadow: "0 0 0 2px $gray-50 !important",
    padding: "0 8px !important",
    position: "initial !important",

    "&:hover": {
      borderColor:
        "var(--colors-patient-details-edit-dropdown-hover) !important",
    },

    span: {
      "&.ant-select-selection-item": {
        fontFamily: "Open Sans",
        fontSize: "12px",
        borderRadius: "14px !important",
        lineHeight: "14px",
        height: "16px",
        marginTop: "-4px",
        marginBottom: "3px",
      },
    },
  },

  ".ant-select-item-option": {
    ".ant-select-item-option-content": {
      fontSize: "12px !important",
      fontWeight: "400",
    },
  },
});

export const PatientMenuCardContainer = styled("div", {
  width: "100%",
  minHeight: 80,

  padding: "16px 22px 16px 28px",

  borderBottom: "1px solid $gray-40",

  fontFamily: "$openSans",

  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",

  "&[dragging='true']": {
    backgroundColor: "$gray-60",
    opacity: "0.7",
  },

  variants: {
    isVisible: {
      true: {
        backgroundColor: "$white",
      },
      false: {
        backgroundColor: "$gray-30",
      },
    },
  },
});

export const PatientMenuCardContent = styled("div", {
  width: "100%",

  p: {
    fontSize: "12px",
    fontWeight: "600",
    color: "$gray-90",
    paddingLeft: 8,
  },

  button: {
    width: "100%",
    marginTop: 4,
    padding: "3px 8px 4px",

    fontFamily: "$openSans",
    fontSize: "12px",
    fontWeight: "400",

    textAlign: "left",

    border: "none",
    borderRadius: 6,

    color: "$gray-90",
    backgroundColor: "transparent",

    "&:hover": {
      cursor: "pointer",
      // backgroundColor: "$primary-20",
    },
  },
});

export const PatientMenuCardControls = styled("div", {
  display: "flex",
  alignItems: "center",

  columnGap: "$8",
});

export const GrabButton = styled("button", {
  cursor: "grab",

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  border: "none",
  borderRadius: "50%",
  backgroundColor: "transparent",

  width: 28,
  height: 28,
  padding: 8,

  "&:active": {
    cursor: "grabbing",
  },
});

export const VisibilityButton = styled(IconButton, {
  width: "22px !important",
  height: "22px !important",
  variants: {
    isActive: {
      true: {
        backgroundColor: "$gray-60",
      },
      false: {
        backgroundColor: "$success-light",

        "&:hover": {
          backgroundColor: "$success-light",
          opacity: 0.6,
        },
      },
    },
  },
});

export const CopyContainer = styled("span", {
  padding: "4px 7px",
  borderRadius: "50%",

  "&:hover": {
    cursor: "pointer",
    backgroundColor: "$gray-30",
  },
});
